import React, { useEffect, useState } from 'react';
import SeloDeComentarios from '../../../components/comentarios/SeloDeComentarios';

import Paginacao from '../../../components/paginacao/Paginacao';
// O estilo da paginação vive no CSS da Recepção — é onde o design de tabela do sistema mora, e o
// próprio componente manda importá-lo. Import explícito: contar com outra tela tê-lo carregado é o
// acoplamento invisível do BUG-V74.
import '../../recepcao/pages/Recepcao.css';
// `premium-text-input` (a busca por placa) e `select-wrapper` (os filtros de status da at-110)
// vivem aqui. O import é EXPLÍCITO pelo mesmo motivo acima: o campo de busca já usava a classe
// contando com o bundle de outra tela tê-la carregado, que é o BUG-V74 esperando acontecer.
import '../../../components/modal/contac/filtro/ModalFiltro.css';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import type { BlocoCarregado } from '../hooks/useComprasDashboard';
import type {
  CompradoresCompras,
  EntregaNoPrazo,
  EvolucaoMensalCompras,
  FilaPorComprador,
  FiltrosCompras,
  FornecedoresCompras,
  ItensMaisRequisitados,
  IdentificacaoDeOficina,
  OficinasCompras,
  RequisicoesPorPlaca,
  RequisicoesPorCentroCusto,
  RequisicoesPorSolicitante,
  RecorteDeRequisicao,
  ResumoCompras,
  UltimasRequisicoes,
} from '../../../api/comprasDashboardApi';
import { fetchRequisicoesDoRecorte } from '../../../api/comprasDashboardApi';
import { SITUACOES_DE_REQUISICAO, rotuloDaSituacao } from '../situacoes';
import { formatarDataPura, formatarInstante } from '../formatarData';
import SelecaoMultiplaModal from './SelecaoMultiplaModal';
import type { RecorteAberto } from './RequisicoesDoRecorteModal';
import {
  corDaSerie,
  formatarMoeda,
  formatarNumero,
  TINTA_GRAFICO,
} from '../../../shared/ui/chart-palette';
import '../../../shared/feedback/aviso-de-tela.css';
import {
  AreaEstado,
  CardGrafico,
  EIXO_LINHA,
  EIXO_TICK,
  ESTILO_TOOLTIP,
  Kpi,
  Legenda,
} from '../../../shared/ui/GraficoBase';

/**
 * Os blocos do Dashboard de Compras (at-100).
 *
 * ⚠️ Tudo aqui é montado com as peças compartilhadas do sistema (`GraficoBase`, `dashboard-base.css`,
 * `chart-palette`): a primeira versão desta tela trouxe CSS próprio e ficou visualmente destoante
 * das outras — o painel tem de parecer o mesmo produto que o Dashboard Oficina e o da Associação.
 *
 * 🔴 Regra que vale para TODOS os blocos: bloco com erro mostra a faixa e **não desenha número
 * nenhum**. Zero aqui é afirmação de negócio ("não há requisição pendente", "nenhuma compra no
 * mês"), e ninguém pode chegar a ela por causa de uma falha de rede (at-60).
 */

/** Faixa de erro padrão do sistema. */
export function FaixaDeErro({ oQue }: { oQue: string }) {
  return (
    <p className="erro-de-tela">
      Não foi possível carregar {oQue}. Recarregue a página; se persistir, acione a TI.
    </p>
  );
}

/** Texto do estado vazio de cada bloco — quem decide a frase é a tela, nunca o componente. */
const SEM_DADO = 'Sem registros no período e no setor escolhidos.';

/**
 * A faixa que diz quais filtros do painel aquele bloco NÃO aplica (at-111).
 *
 * 🔴 Existe como componente porque são três blocos dizendo a mesma coisa, e três redações
 * divergiriam no primeiro ajuste: filtro aplicado que não muda o número é lido como **resposta**
 * ("a oficina compra tanto quanto a empresa toda", "estas são as entregas dos outros setores"), e
 * a frase que desfaz essa leitura é a parte que não pode variar.
 *
 * ⚠️ O `porque` é por bloco porque o motivo é mesmo outro: na evolução e nos fornecedores a fonte
 * é o **pedido de compra**, que não registra setor; na entrega no prazo é a **movimentação de
 * estoque cruzada com a nota fiscal**, que não registra nem setor nem comprador.
 */
export function FaixaFiltrosNaoAplicados({
  filtros,
  porque,
}: {
  filtros?: string[] | null;
  porque: string;
}) {
  if (!filtros?.length) return null;
  return (
    <p className="aviso-de-tela">
      O filtro de {filtros.join(' e ')} não se aplica a este bloco: {porque}
    </p>
  );
}

/**
 * Abre o detalhamento de um card. O bloco só diz QUAL recorte foi clicado.
 *
 * 🔴 Não passa o número que estava na tela, e isso é deliberado: quem conta é o servidor, pelo
 * mesmo `where` que produziu o card (`whereDoRecorte`). Mandar o número junto criaria dois valores
 * para a mesma pergunta — e a "solução" para isso seria um aviso de divergência, que é a confissão
 * de que o sistema pode mentir (decisão do Victor, 11/09/2026).
 */
export type AbrirRecorte = (aberto: RecorteAberto) => void;

export function KpisCompras({
  estado,
  aoDetalhar,
}: {
  estado: BlocoCarregado<ResumoCompras>;
  aoDetalhar: AbrirRecorte;
}) {
  if (estado.erro) return <FaixaDeErro oQue="os indicadores de compras" />;

  const kpis = estado.dados?.kpis;
  const carregando = estado.carregando;
  const funil = estado.dados?.funil ?? [];

  /*
   * at-124 (Victor, 08/10/2026): o bloco "Fluxo de compras" saiu, e as etapas que só ele tinha
   * viraram cards desta fileira — uma coisa só, sem repetir as quatro que os dois mostravam.
   *
   * O número vem da linha do `funil` da MESMA resposta, e o `estado` dela já é o id do recorte:
   * o clique abre exatamente a lista que a coluna do fluxo abria. O rótulo padrão só serve para o
   * card não sumir enquanto carrega; carregado, etapa que o backend não mandou não vira card.
   */
  const cardDoFluxo = (estadoDaLinha: string, rotuloPadrao: string) => {
    const linha = funil.find((f) => f.estado === estadoDaLinha);
    if (!linha && !carregando) return null;
    const rotulo = linha?.rotulo ?? rotuloPadrao;
    return (
      <Kpi
        key={estadoDaLinha}
        rotulo={rotulo}
        valor={carregando ? '' : formatarNumero(linha?.total ?? 0)}
        apoio={linha?.legenda}
        carregando={carregando}
        indisponivel={linha?.indisponivel?.curto}
        detalheRotulo={`Ver as requisições em ${rotulo.toLowerCase()}`}
        onDetalhar={() =>
          aoDetalhar({
            recorte: estadoDaLinha as RecorteDeRequisicao,
            titulo: rotulo,
            legenda: linha?.legenda,
          })
        }
      />
    );
  };

  return (
    <>
    <div className="do-kpis">
      <Kpi
        rotulo="Total de requisições"
        valor={carregando ? '' : formatarNumero(kpis?.totalRequisicoes ?? 0)}
        apoio="cadastradas no período"
        carregando={carregando}
        detalheRotulo="Ver todas as requisições do período"
        onDetalhar={() =>
          aoDetalhar({
            recorte: 'total',
            titulo: 'Total de requisições',
            legenda: 'cadastradas no período',
          })
        }
      />
      {/*
        at-123: as linhas de item das MESMAS requisições do card ao lado (o servidor conta pelo
        mesmo recorte). Sem clique: o painel não tem lista de itens para abrir.
      */}
      <Kpi
        rotulo="Total de itens"
        valor={carregando ? '' : formatarNumero(kpis?.totalItens ?? 0)}
        apoio="itens dessas requisições"
        carregando={carregando}
      />
      {/* Identificadas pelo texto "CAMINHO VIP" na justificativa da requisição. */}
      <Kpi
        rotulo="Total de requisições - Caminho VIP"
        valor={carregando ? '' : formatarNumero(kpis?.caminhoVip ?? 0)}
        apoio="requisições do Caminho VIP no período"
        carregando={carregando}
        detalheRotulo="Ver as requisições do Caminho VIP"
        onDetalhar={() =>
          aoDetalhar({
            recorte: 'caminho_vip',
            titulo: 'Total de requisições - Caminho VIP',
            legenda: 'requisições do Caminho VIP no período',
          })
        }
      />
      {/* Os itens das mesmas requisições do card ao lado. Sem clique, como "Total de itens". */}
      <Kpi
        rotulo="Total de itens - Caminho VIP"
        valor={carregando ? '' : formatarNumero(kpis?.caminhoVipItens ?? 0)}
        apoio="itens dessas requisições"
        carregando={carregando}
      />
      {cardDoFluxo('antes_de_compras', 'Antes de Compras')}
      <Kpi
        rotulo="Aguardando atendimento"
        valor={carregando ? '' : formatarNumero(kpis?.aguardandoAtendimento ?? 0)}
        /*
         * ⚠️ Indisponível, a linha de apoio carrega a EXPLICAÇÃO inteira: o cartão do topo é
         * largo, e é o lugar onde ela cabe sem deformar a fileira. Na coluna do funil vai só o
         * texto curto.
         */
        apoio={
          kpis?.aguardandoAtendimentoIndisponivel?.explicacao ?? 'requisições sem comprador'
        }
        carregando={carregando}
        /*
         * 🔴 at-115: com um comprador escolhido nos filtros, este card não tem o que contar — ele
         * é, por definição, "sem comprador". O texto do servidor substitui o número; `0` ali seria
         * lido como "não há ninguém aguardando".
         */
        indisponivel={kpis?.aguardandoAtendimentoIndisponivel?.curto}
        detalheRotulo="Ver as requisições aguardando atendimento"
        onDetalhar={() =>
          aoDetalhar({
            recorte: 'pendente_sem_comprador',
            titulo: 'Aguardando atendimento',
            legenda: 'requisições sem comprador',
          })
        }
      />
      <Kpi
        rotulo="Em atendimento"
        valor={carregando ? '' : formatarNumero(kpis?.emAtendimento ?? 0)}
        apoio="já com comprador"
        carregando={carregando}
        detalheRotulo="Ver as requisições em atendimento"
        onDetalhar={() =>
          aoDetalhar({
            recorte: 'pendente_com_comprador',
            titulo: 'Em atendimento',
            legenda: 'já com comprador',
          })
        }
      />
      {/* Em cotação e Em negociação voltaram a ser cards separados (Victor, 09/10/2026). */}
      {cardDoFluxo('em_cotacao', 'Em cotação')}
      {cardDoFluxo('em_negociacao', 'Em negociação')}
      {cardDoFluxo('pedido_gerado', 'Pedido gerado')}
      {cardDoFluxo('parcialmente_atendida', 'Parcialmente atendida')}
      <Kpi
        rotulo="Atendidas"
        valor={carregando ? '' : formatarNumero(kpis?.atendidasNaJanela ?? 0)}
        apoio={kpis?.definicaoAtendidas}
        carregando={carregando}
        detalheRotulo="Ver as requisições atendidas no período"
        onDetalhar={() =>
          aoDetalhar({
            recorte: 'atendida',
            titulo: 'Atendidas',
            legenda: kpis?.definicaoAtendidas,
          })
        }
      />
      {cardDoFluxo('cancelada', 'Canceladas')}
      {/* "Outros estados" só existe quando o ERP devolve situação desconhecida. */}
      {funil.some((f) => f.estado === 'outro') ? cardDoFluxo('outro', 'Outros estados') : null}
      {/*
        🔴 TEMPO DE ATENDIMENTO SAIU DESTA FILEIRA — at-116, decisão do Victor (27/09/2026).
        Ver `TempoPorCompradorBloco`, que é onde a espera está declarada.

        O motivo não é a amostra pequena: é que o indicador era alimentado **só pela observação do
        próprio SOAC**. O Nasajon não guarda quando a requisição mudou de situação, então o espelho
        anotava cada transição que via — e, com o sync parado (já aconteceu: `BUG-008`, crons que
        rodaram 2 dias em 30), a mudança só é datada quando a varredura volta. O número não fica
        ausente: fica **maior do que foi**, sem nada na tela denunciando.

        ⚠️ Um indicador que depende da disponibilidade do próprio observador não pode ser publicado
        ao lado de contagens que vêm direto do ERP. A pergunta está com a Nasajon desde 22/09/2026.
      */}
      {/*
        ⚠️ "Entrega no prazo" existe, mas em bloco PRÓPRIO (`EntregaNoPrazoBloco`) e não aqui: ele
        cobre só a oficina interna, e a ressalva não cabe num KPI de uma linha. Pôr o percentual
        nesta fileira, ao lado de números que valem para a operação inteira, faria o leitor supor o
        mesmo escopo — que é justamente o engano que a feature precisa evitar.
      */}
      {/*
        at-117 — o rótulo era "Idade das pendentes" e não dizia o que o número mede (decisão do
        Victor, 27/09/2026). São duas coisas não óbvias que o nome novo carrega: a contagem parte
        da **data de cadastro no Nasajon**, e "pendente" aqui significa **ainda não virou pedido de
        compra** — quem lia "31 dias" tendia a entender "a peça demora 31 dias para chegar".

        ⚠️ O campo da API (`idadePendentesDias`) NÃO foi renomeado: ele é contrato (matriz +
        shape e2e), e trocá-lo custaria os dois repos para mudar um texto.
      */}
      <Kpi
        rotulo="Tempo na fila de Compras"
        valor={
          carregando || kpis?.idadePendentesDias.mediana == null
            ? '—'
            : `${kpis.idadePendentesDias.mediana} d`
        }
        /*
          at-120 (Victor, 05/10/2026): um número só, das requisições em aberto E das que já viraram
          pedido. A linha de apoio diz a composição — sem ela, "23 d" não diz se é fila parada ou
          compra concluída.
        */
        apoio={
          kpis?.idadePendentesDias
            ? `mediana · ${formatarNumero(kpis.idadePendentesDias.emAberto ?? 0)} em aberto, ${formatarNumero(kpis.idadePendentesDias.finalizadas ?? 0)} viraram pedido`
            : 'mediana'
        }
        /*
          A hint responde "o que este número mede?" sem abrir o drawer — a pergunta que o Victor
          fez em 27/09/2026 olhando o card, com o rótulo novo já na tela.

          🔴 Ela NOMEIA os estados (decisão dele, na mesma conversa), em vez de descrevê-los por
          negação ("o que ainda não virou pedido"): quem lê o card conhece os estados do ERP pelo
          nome — são os mesmos rótulos do fluxo logo abaixo —, e a lista fecha a pergunta em vez de
          deixar o leitor deduzir quais entram.

          🔴 **A ordem da frase não é estética.** O KPI cruza DOIS campos do ERP (`situacao` e
          `estado_workflow`), mas o segundo só morde em "Pendente": medido em 27/09/2026, as 8 em
          cotação e as 4 em negociação são **100% aprovadas**. Escrever "Pendente (já aprovada), Em
          cotação e Em negociação" obrigava a explicar que existem dois campos — e "Pendente (já
          aprovada)" ainda soa contraditório para quem não sabe disso. Pondo as pendentes por
          último, a ressalva cai exatamente onde ela age.
        */
        hint={textoDaRegraDaFila(kpis?.atePedidoObservadoDesde ?? null)}
        carregando={carregando}
        detalheRotulo="Ver as requisições, da mais antiga para a mais recente"
        onDetalhar={() =>
          aoDetalhar({
            // ⚠️ Este card é uma MEDIANA em dias, não uma contagem: o clique abre a população que
            // ela mede — em aberto e já convertidas em pedido, com a coluna "Fila" separando.
            recorte: 'pendentes',
            titulo: 'Tempo na fila de Compras, da mais antiga para a mais recente',
            legenda: 'a mediana do card é calculada sobre estas',
          })
        }
      />
    </div>
    {/* at-124: o rodapé do antigo "Fluxo de compras" desceu junto com os cards. */}
    <ResiduoForaDoPeriodo funil={funil} aoDetalhar={aoDetalhar} />
    </>
  );
}

/**
 * O resíduo dos estados-cemitério: requisições com pedido gerado há muito tempo que ninguém baixou
 * no ERP. Não é fila de trabalho — somá-las aos cards faria o painel mostrar milhares de itens
 * "aguardando entrega". Era o rodapé do "Fluxo de compras", que saiu na at-124.
 */
export function ResiduoForaDoPeriodo({
  funil,
  aoDetalhar,
}: {
  funil: ResumoCompras['funil'];
  aoDetalhar: AbrirRecorte;
}) {
  const residuo = funil.filter((f) => (f.foraDaJanela ?? 0) > 0);
  if (!residuo.length) return null;
  return (
    <p className="do-nota-janela">
      Fora do período:{' '}
      {residuo.map((r, i) => (
        <React.Fragment key={r.estado}>
          {i > 0 ? ' e ' : ''}
          {/*
            O resíduo também é clicável: são os números maiores da tela (447 e 1.457 na medição de
            11/09/2026), e "requisições antigas em aberto" só vira ação quando se sabe quais são.
            Recorte próprio (`_fora_da_janela`), porque é o complemento do card irmão — reusar o
            dele abriria a fila em vez do cemitério.
          */}
          <button
            type="button"
            className="dc-link-residuo"
            onClick={() =>
              aoDetalhar({
                recorte: `${r.estado}_fora_da_janela` as RecorteDeRequisicao,
                titulo: `${r.rotulo} — fora do período`,
                legenda: 'requisições antigas que seguem em aberto no ERP',
              })
            }
          >
            {formatarNumero(r.foraDaJanela ?? 0)}
          </button>
          {` em "${r.rotulo.toLowerCase()}"`}
        </React.Fragment>
      ))}
      . São requisições antigas que seguem em aberto no ERP.
    </p>
  );
}

/**
 * A regra do "Tempo na fila de Compras" em linguagem de quem usa o Nasajon — compartilhada pelo
 * KPI e pelo card por comprador, para os dois nunca dizerem coisas diferentes (at-120).
 *
 * ⚠️ A data de cobertura não é enfeite: o elo pedido → requisição só existe no espelho a partir da
 * primeira negociação sincronizada, e uma requisição que virou pedido antes disso fica de fora.
 */
export function textoDaRegraDaFila(observadoDesde: string | null): string {
  const regra =
    'Dias desde o cadastro da requisição no Nasajon: até hoje, se ainda está na fila (Em cotação, ' +
    'Em negociação e as Pendentes já aprovadas); até a emissão do primeiro pedido de compra, se já ' +
    'virou pedido. Pedidos cancelados não contam.';
  if (!observadoDesde) return regra;
  const desde = new Date(observadoDesde).toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' });
  return `${regra} As que viraram pedido são contadas a partir de ${desde}.`;
}

/**
 * Uma coluna do funil — compartilhada pelo fluxo de requisições e pelo de pedidos (at-121).
 *
 * ⚠️ Extraída, e não copiada: o funil de pedidos é o mesmo desenho sobre outra entidade, e duas
 * cópias do markup divergiriam no primeiro ajuste (regra do catálogo de UI).
 *
 * `<button>` de verdade, como o `Kpi` compartilhado faz — não um `<div>` com `onClick`: alcançável
 * por teclado e anunciado como algo que abre.
 */
export function ColunaDoFunil({
  rotulo,
  total,
  maior,
  legenda,
  descricaoDoClique,
  onClick,
}: {
  rotulo: string;
  total: number;
  /** O maior total do funil — a escala da barra. */
  maior: number;
  /**
   * at-117: o que a coluna reúne quando o rótulo não basta ("Antes de Compras" = em aprovação,
   * não iniciada ou reprovada; "Cancelados" = não contam no total).
   */
  legenda?: string;
  descricaoDoClique: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className="do-kpi do-kpi-clicavel"
      title={descricaoDoClique}
      aria-label={descricaoDoClique}
      onClick={onClick}
    >
      <div className="do-kpi-rotulo">{rotulo}</div>
      <div className="do-kpi-valor">{formatarNumero(total)}</div>
      <div className="do-medidor">
        <div className="do-medidor-preenchimento" style={{ width: `${(total / Math.max(1, maior)) * 100}%` }} />
      </div>
      {legenda ? <div className="do-kpi-apoio">{legenda}</div> : null}
    </button>
  );
}

export function EvolucaoMensal({
  estado,
  aoAbrirMes,
}: {
  estado: BlocoCarregado<EvolucaoMensalCompras>;
  aoAbrirMes: (mes: string) => void;
}) {
  if (estado.erro) return <FaixaDeErro oQue="a evolução mensal de compras" />;

  const dados = estado.dados;
  const temMovimento = (dados?.meses ?? []).some((m) => m.pedidos > 0 || m.valor > 0);

  return (
    <CardGrafico
      titulo="Evolução mensal"
      // Barra de gráfico não parece clicável — sem dizer, ninguém descobre.
      descricao={
        dados?.periodoAplicado
          ? 'Pedidos gerados e valor comprado no período escolhido, pela data de emissão do pedido. Clique numa coluna para ver os pedidos do mês.'
          : 'Pedidos gerados e valor comprado nos últimos 12 meses, pela data de emissão do pedido. Clique numa coluna para ver os pedidos do mês.'
      }
    >
      {/*
        ⚠️ at-115 — o que fica aqui é só o centro de custo sem cadastro sincronizado. O comprador
        SAIU desta faixa porque passou a ser aplicado de verdade (pelo elo pedido → negociação →
        requisição). Declarar um filtro que agora funciona seria mentir na direção oposta.
      */}
      <FaixaFiltrosNaoAplicados
        filtros={dados?.filtrosNaoAplicados}
        porque="o cadastro de locais de uso ainda não foi sincronizado."
      />
      {/*
        🔴 Os pedidos que nenhum recorte por origem alcança — compra direta, sem requisição. Sem
        este aviso, o total daqui fica menor que o da aba Pedidos e os dois parecem discordar.
      */}
      {dados?.semOrigemConhecida ? (
        <p className="aviso-de-tela">
          {formatarNumero(dados.semOrigemConhecida)}{' '}
          {dados.semOrigemConhecida === 1 ? 'pedido do período ficou' : 'pedidos do período ficaram'}{' '}
          fora deste recorte: são compras diretas, sem requisição de origem, e por isso não
          pertencem a nenhum comprador nem centro de custo.
        </p>
      ) : null}
      {dados?.serieCortada ? (
        <p className="aviso-de-tela">
          O período escolhido é longo demais para o gráfico: estão sendo exibidos apenas os meses
          mais recentes.
        </p>
      ) : null}
      {estado.carregando || !temMovimento ? (
        <AreaEstado
          carregando={estado.carregando}
          mensagem="Nenhum pedido de compra sincronizado ainda para este período."
        />
      ) : (
        <>
          <div className="do-plot">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={dados?.meses ?? []} margin={{ left: 8, right: 16 }}>
                <CartesianGrid stroke={TINTA_GRAFICO.grade} vertical={false} />
                <XAxis
                  dataKey="mes"
                  tickFormatter={(mes: string) => mes.slice(5)}
                  tick={EIXO_TICK}
                  axisLine={EIXO_LINHA}
                  tickLine={false}
                />
                <YAxis yAxisId="qtd" tick={EIXO_TICK} axisLine={EIXO_LINHA} tickLine={false} allowDecimals={false} />
                <YAxis
                  yAxisId="valor"
                  orientation="right"
                  tick={EIXO_TICK}
                  axisLine={EIXO_LINHA}
                  tickLine={false}
                  tickFormatter={(v: number) => formatarMoeda(v)}
                />
                <Tooltip
                  {...ESTILO_TOOLTIP}
                  formatter={(valor: number, nome: string) =>
                    nome === 'Valor comprado' ? formatarMoeda(valor) : formatarNumero(valor)
                  }
                />
                {/*
                  A coluna abre os pedidos daquele mês.

                  ⚠️ O clique é na BARRA (`onClick` do `Bar`), não no gráfico inteiro: clicar num
                  ponto vazio do plot não deve abrir o mês vizinho. O Recharts entrega o dado da
                  barra clicada no primeiro argumento.

                  ⚠️ Só a barra é clicável, não a LINHA de valor: as duas descrevem o mesmo mês, e
                  dois alvos para a mesma ação confundiriam sem acrescentar nada.
                */}
                <Bar
                  yAxisId="qtd"
                  dataKey="pedidos"
                  name="Pedidos gerados"
                  fill={corDaSerie(0)}
                  cursor="pointer"
                  onClick={(ponto: { mes?: string }) => {
                    if (ponto?.mes) aoAbrirMes(ponto.mes);
                  }}
                />
                <Line
                  yAxisId="valor"
                  type="monotone"
                  dataKey="valor"
                  name="Valor comprado"
                  stroke={corDaSerie(1)}
                  strokeWidth={2}
                  dot={false}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
          <Legenda
            itens={[
              { rotulo: 'Pedidos gerados', cor: corDaSerie(0) },
              { rotulo: 'Valor comprado', cor: corDaSerie(1) },
            ]}
          />
          {/* Totais do período inteiro: a série diz como variou, estes dizem quanto foi. */}
          <p className="do-nota-janela">
            No período: <strong>{formatarNumero(dados?.totais.pedidos ?? 0)}</strong> pedidos gerados
            e <strong>{formatarMoeda(dados?.totais.valor ?? 0)}</strong> comprados.
            {dados && dados.cancelados > 0
              ? ` ${formatarNumero(dados.cancelados)} pedidos cancelados não entram na conta.`
              : ''}
          </p>
        </>
      )}
    </CardGrafico>
  );
}

/**
 * O filtro de status de um card (at-110).
 *
 * 🔴 O rótulo DIZ o escopo ("só deste card"), e isso não é zelo excessivo: o funil, logo acima,
 * mostra as mesmas situações e recorta a tela inteira. Dois controles com o mesmo vocabulário e
 * alcances diferentes é exatamente o que produziu o `BUG-R47` — lá, o seletor de esteira de um card
 * filtrava as linhas da matriz enquanto o do painel filtrava a população, e o assistente afirmou
 * "não há horas registradas" sobre uma esteira com 46 h na tela.
 *
 * ⚠️ "Todos os status" é o padrão e é uma OPÇÃO de verdade, não ausência de escolha: sem ela, quem
 * filtrou não teria como voltar sem recarregar a página.
 */
export function FiltroDeSituacao({
  valores,
  onMudar,
  idDoCampo,
  opcoes = SITUACOES_DE_REQUISICAO,
  tituloDoModal = 'Status das requisições',
  rotulo = 'Status (só deste card)',
}: {
  valores: string[];
  onMudar: (situacoes: string[]) => void;
  idDoCampo: string;
  /**
   * at-121: o vocabulário do card. Padrão: as situações da REQUISIÇÃO; os cards da aba Pedidos
   * passam as do pedido, que são outras (e só três).
   */
  opcoes?: Array<{ valor: string; rotulo: string }>;
  tituloDoModal?: string;
  /** at-129: o filtro de chegada reusa este componente, com outro nome no campo. */
  rotulo?: string;
}) {
  const [aberto, setAberto] = useState(false);
  const rotuloDe = (valor: string) => opcoes.find((o) => o.valor === valor)?.rotulo ?? valor;

  return (
    <div className="compras-filtro-card">
      <span id={`${idDoCampo}-rotulo`}>{rotulo}</span>
      {/*
        at-112: botão + modal de caixas de seleção, no lugar do menu de um valor só.

        ⚠️ O rótulo do botão diz o que está valendo, e não "Filtrar": com o modal fechado ele é o
        ÚNICO indício de que o card está recortado — é a mesma razão pela qual o modal de filtros
        do Contac não tem chips, mas a tela tem contador.
      */}
      <button
        type="button"
        id={idDoCampo}
        className="compras-filtro-botao"
        aria-labelledby={`${idDoCampo}-rotulo ${idDoCampo}`}
        onClick={() => setAberto(true)}
      >
        {rotuloDoFiltroDeStatus(valores, rotuloDe)}
      </button>
      {/* Montagem condicional: cada abertura recomeça do que está aplicado (padrão do projeto). */}
      {aberto ? (
        <SelecaoMultiplaModal
          titulo={tituloDoModal}
          opcoes={opcoes.map((s) => ({ valor: s.valor, rotulo: s.rotulo }))}
          selecionados={valores}
          onAplicar={(escolhidos) => {
            onMudar(escolhidos);
            setAberto(false);
          }}
          onFechar={() => setAberto(false)}
        />
      ) : null}
    </div>
  );
}

/**
 * O que o botão do filtro mostra (at-112).
 *
 * ⚠️ Com um status só, mostra o NOME dele — "1 selecionado" obrigaria a abrir o modal para saber
 * o que está valendo, num card que já é pequeno. A partir de dois, a contagem é o que cabe numa
 * linha.
 */
function rotuloDoFiltroDeStatus(
  valores: string[],
  rotuloDe: (valor: string) => string = rotuloDaSituacao,
): string {
  if (valores.length === 0) return 'Todos os status';
  if (valores.length === 1) return rotuloDe(valores[0]);
  return `${valores.length} status selecionados`;
}

/** A frase que descreve o recorte do card, para reaproveitar nos estados vazios e nas notas. */
function descricaoDoFiltroDeStatus(valores: string[]): string {
  if (valores.length === 0) return '';
  if (valores.length === 1) return ` com status "${rotuloDaSituacao(valores[0])}"`;
  return ` nos ${valores.length} status escolhidos`;
}

/**
 * O rótulo do recorte do card de oficinas (at-117), para a faixa de cobertura.
 *
 * ⚠️ Em minúsculas porque entra no meio de uma frase ("Mostrando 14 de 653 — somente oficinas não
 * identificadas.").
 */
function rotuloDaIdentificacao(identificacao: IdentificacaoDeOficina): string {
  if (identificacao === 'identificadas') return 'somente oficinas identificadas';
  if (identificacao === 'nao_identificadas') return 'somente oficinas não identificadas';
  return 'todas as oficinas';
}

export function CargaCompradores({
  estado,
  situacoes = [],
  onSituacoes,
  aoAbrirComprador,
  aoAbrirItens,
  aoDetalhar,
}: {
  estado: BlocoCarregado<CompradoresCompras>;
  /** Os status escolhidos no filtro do card. Lista vazia = todos (o padrão). */
  situacoes?: string[];
  onSituacoes?: (situacoes: string[]) => void;
  /** Abre as requisições que compõem a linha do comprador. */
  aoAbrirComprador?: (comprador: { id: string; nome: string | null }) => void;
  /**
   * Abre as PEÇAS que compõem "Itens distintos" — at-115. Só existe no modo recortado: sem
   * comprador, o número do card é a soma dos distintos por pessoa, e uma lista global não bateria
   * com ele (ver a rota no backend, que recusa a chamada sem comprador).
   */
  aoAbrirItens?: (comprador: { id: string; nome: string }) => void;
  /** Abre um recorte do painel — usado por "Em aberto", que é o recorte `pendentes`. */
  aoDetalhar?: AbrirRecorte;
}) {
  if (estado.erro) return <FaixaDeErro oQue="a carga por comprador" />;

  const dados = estado.dados;
  const compradores = dados?.compradores ?? [];
  const maior = Math.max(1, ...compradores.map((c) => c.itensDistintos));

  /*
   * 🔴 at-115 — com um comprador no filtro do topo, o card TROCA DE FORMA, e não só de conteúdo.
   *
   * Decisão do Victor (24/09/2026). O motivo é de leitura: filtrado, o gráfico teria uma barra só,
   * e barra única não compara nada — que é a única coisa que um gráfico de barras faz. Ele vira o
   * resumo daquela pessoa, com os mesmos três números que a linha dela carregava.
   *
   * ⚠️ Antes desta entrega o card ignorava o filtro **em silêncio** e mostrava todos os
   * compradores enquanto o resto do painel estava recortado por um só — era o mesmo defeito de
   * sobrescrita do `where` que apagava o comprador no backend, não uma escolha de desenho.
   */
  const recorte = dados?.recortadoPor ?? null;
  if (recorte) {
    const dele = compradores.find((c) => c.analistaCompra === recorte.id);
    const nome = recorte.nome ?? 'comprador sem nome cadastrado';
    return (
      <CardGrafico titulo={`Carga de ${nome}`}>
        <p className="do-nota-janela">
          Recortado pelo filtro de comprador. Para comparar a carga entre as pessoas, remova esse
          filtro.
        </p>
        {onSituacoes ? (
          <FiltroDeSituacao
            valores={situacoes}
            onMudar={onSituacoes}
            idDoCampo="filtro-status-compradores"
          />
        ) : null}
        {estado.carregando || !dele ? (
          /*
           * ⚠️ A mensagem NOMEIA a pessoa e o recorte: "Sem registros no período" aqui seria lido
           * como "esta pessoa não trabalha", quando o que houve foi um período em que ela não
           * cadastrou requisição. É a mesma regra do card indisponível — vazio explicado, nunca
           * vazio cru (at-60).
           */
          <AreaEstado
            carregando={estado.carregando}
            mensagem={`${nome} não tem requisições${descricaoDoFiltroDeStatus(situacoes)} no período e no recorte escolhidos.`}
          />
        ) : (
          /*
           * 🔴 Os TRÊS números abrem, e cada um abre o que ele conta (at-115, pedido do Victor).
           *
           * ⚠️ Os recortes são diferentes entre si, e é isso que quem mexer aqui precisa saber:
           *   - **Itens distintos** conta CÓDIGOS de peça, então abre a lista de peças — não a de
           *     requisições (5 peças em 10 requisições: a lista não bateria com o número);
           *   - **Requisições** leva o status do card, porque foi com ele que a barra contou;
           *   - **Em aberto** NÃO leva o status: a coluna descreve a fila viva da pessoa e não
           *     segue o filtro do card (regra da at-110). Levar o status faria a lista encolher
           *     sem o número encolher junto.
           */
          <div className="do-kpis">
            <Kpi
              rotulo="Itens distintos"
              valor={formatarNumero(dele.itensDistintos)}
              apoio="códigos de peça diferentes"
              detalheRotulo={`Ver as peças pedidas por ${nome}`}
              onDetalhar={aoAbrirItens ? () => aoAbrirItens({ id: recorte.id, nome }) : undefined}
            />
            <Kpi
              rotulo="Requisições"
              valor={formatarNumero(dele.requisicoes)}
              apoio={`no período${descricaoDoFiltroDeStatus(situacoes)}`}
              detalheRotulo={`Ver as requisições de ${nome}`}
              onDetalhar={
                aoAbrirComprador
                  ? () => aoAbrirComprador({ id: dele.analistaCompra, nome: dele.nome })
                  : undefined
              }
            />
            <Kpi
              rotulo="Em aberto"
              valor={formatarNumero(dele.pendentes)}
              apoio="ainda sem pedido gerado"
              detalheRotulo={`Ver o que ${nome} tem em aberto`}
              onDetalhar={
                aoDetalhar
                  ? () =>
                      aoDetalhar({
                        // O MESMO recorte que produziu o número: `pendentes` é
                        // `situacao IN (abertas)` na janela, e o comprador já está no filtro do
                        // painel — por isso não vai em `filtrosExtras`.
                        recorte: 'pendentes',
                        titulo: `Em aberto — ${nome}`,
                        legenda: 'ainda sem pedido gerado, da mais antiga para a mais recente',
                      })
                  : undefined
              }
            />
          </div>
        )}
      </CardGrafico>
    );
  }

  // Sem descrição, por pedido do Victor (21/09/2026) — o título já diz o que o card mostra.
  return (
    <CardGrafico titulo="Carga por comprador">
      {/*
        at-105: o nome vem do próprio Nasajon (`GVN/EXE/IDM/users`), sincronizado todo dia. O aviso
        deixou de mandar cadastrar à mão — a tela onde se fazia isso não existe mais.

        ⚠️ Ele continua existindo porque o caso continua possível: um código que não está no
        diretório (pessoa removida de lá, por exemplo) aparece sem nome, e sumir com o aviso faria
        a lacuna passar por normal. Cobertura medida em 15/09/2026: 77 de 77.
      */}
      {dados && dados.semNome > 0 ? (
        <p className="aviso-de-tela">
          {dados.semNome} {dados.semNome === 1 ? 'comprador aparece' : 'compradores aparecem'} por
          código: não há usuário com esse identificador no cadastro do Nasajon. As requisições dessa
          pessoa continuam contando normalmente.
        </p>
      ) : null}
      {onSituacoes ? (
        <FiltroDeSituacao
          valores={situacoes}
          onMudar={onSituacoes}
          idDoCampo="filtro-status-compradores"
        />
      ) : null}
      {estado.carregando || compradores.length === 0 ? (
        <AreaEstado
          carregando={estado.carregando}
          mensagem={
            situacoes.length
              ? `Nenhuma requisição${descricaoDoFiltroDeStatus(situacoes)} no período e no recorte escolhidos.`
              : SEM_DADO
          }
        />
      ) : (
        <>
        <p className="do-nota-janela">
          No período: <strong>{formatarNumero(dados?.totais.itensDistintos ?? 0)}</strong> itens
          distintos em <strong>{formatarNumero(dados?.totais.requisicoes ?? 0)}</strong> requisições
          {descricaoDoFiltroDeStatus(situacoes)}.
        </p>
        <ul className="compras-barras">
          {compradores.map((c) => {
            const nome = c.nome ?? null;
            return (
              <li key={c.analistaCompra}>
                <div className="compras-barras-topo">
                  {/*
                    O nome vira botão: é o clique que abre QUAIS requisições somam aquela barra.
                    `button` de verdade, e não `span` com onClick, para chegar por teclado — a
                    mesma escolha do `do-celula-link` nas tabelas.
                  */}
                  <span>
                    {aoAbrirComprador ? (
                      <button
                        type="button"
                        className="do-celula-link"
                        onClick={() => aoAbrirComprador({ id: c.analistaCompra, nome })}
                      >
                        {nome ?? `${c.analistaCompra.slice(0, 8)} (sem nome)`}
                      </button>
                    ) : (
                      nome ?? (
                        <>
                          <span className="do-codigo">{c.analistaCompra.slice(0, 8)}</span> sem nome
                        </>
                      )
                    )}
                  </span>
                  <span>
                    {formatarNumero(c.itensDistintos)} itens · {formatarNumero(c.requisicoes)} req.
                  </span>
                </div>
                <div className="do-medidor">
                  <div
                    className="do-medidor-preenchimento"
                    style={{ width: `${(c.itensDistintos / maior) * 100}%` }}
                  />
                </div>
              </li>
            );
          })}
        </ul>
        </>
      )}
    </CardGrafico>
  );
}

/**
 * TEMPO NA FILA POR COMPRADOR — at-119 (pedido do Victor, 02/10/2026).
 *
 * Ocupa o lugar do bloco de espera da at-116, e NÃO é o mesmo indicador. A at-116 media o tempo
 * entre transições de situação observadas pelo SOAC — número que dependia do sync estar no ar, e
 * por isso saiu da tela. Este card usa a métrica do KPI **"Tempo na fila de Compras"**, e todas as
 * datas vêm do ERP; o SOAC só faz a conta.
 *
 * 🔴 **at-120 (Victor, 05/10/2026): "uma coisa só"** — a barra junta as requisições EM ABERTO
 * (cadastro até hoje) e as que já VIRARAM PEDIDO (cadastro até a emissão do primeiro pedido). Até
 * a at-119 só a primeira contava, e quem fechava pedidos velhos ficava com número baixo; agora o
 * que foi comprado continua na barra de quem comprou. A composição aparece ao lado do número.
 *
 * 🔴 Sai da MESMA consulta do KPI (`temposDaFila`, no backend): `total` é o número do topo,
 * e as barras somadas (mais `semComprador`) são a amostra dele. A tela nunca agrupa a lista por
 * conta própria.
 *
 * ⚠️ `tempoPorComprador` (a medição por transições) continua no backend, sem consumidor, até a
 * Nasajon expor o histórico de situações — como `entrega-no-prazo`.
 */
export function FilaPorCompradorBloco({
  estado,
  aoAbrirComprador,
}: {
  estado: BlocoCarregado<FilaPorComprador>;
  /** Abre a fila daquela pessoa — o recorte `pendentes` recortado pelo comprador. */
  aoAbrirComprador?: (comprador: { id: string; nome: string | null }) => void;
}) {
  if (estado.erro) return <FaixaDeErro oQue="o tempo na fila por comprador" />;

  const dados = estado.dados;
  const compradores = dados?.compradores ?? [];
  const maior = Math.max(1, ...compradores.map((c) => c.mediana ?? 0));

  return (
    <CardGrafico
      titulo="Tempo na fila por comprador"
      descricao="Mediana de dias na fila de Compras das requisições de cada comprador."
    >
      {estado.carregando || compradores.length === 0 ? (
        <AreaEstado
          carregando={estado.carregando}
          mensagem={
            dados && dados.semComprador > 0
              ? `Nenhuma requisição com comprador no período. ${dados.semComprador} sem comprador na requisição.`
              : 'Nenhuma requisição na fila de Compras no período e no recorte escolhidos.'
          }
        />
      ) : (
        <>
          {/*
            🔴 at-120 — a regra no próprio card (pedido do Victor, 05/10/2026), a MESMA do KPI do
            topo: em aberto conta até hoje, quem virou pedido conta até a emissão do primeiro
            pedido. `do-nota-janela`, e não `aviso-de-tela`: é a definição do número, não um alerta.
          */}
          <p className="do-nota-janela">
            {textoDaRegraDaFila(dados?.atePedidoObservadoDesde ?? null)}
            {dados && dados.semComprador > 0 ? (
              <>
                {' '}
                Fora das barras: <strong>{formatarNumero(dados.semComprador)}</strong> sem comprador
                na requisição.
              </>
            ) : null}
          </p>
          <ul className="compras-barras">
            {compradores.map((c) => {
              const nome = c.nome ?? null;
              return (
                <li key={c.id}>
                  <div className="compras-barras-topo">
                    <span>
                      {aoAbrirComprador ? (
                        <button
                          type="button"
                          className="do-celula-link"
                          onClick={() => aoAbrirComprador({ id: c.id, nome })}
                        >
                          {nome ?? `${c.id.slice(0, 8)} (sem nome)`}
                        </button>
                      ) : (
                        nome ?? (
                          <>
                            <span className="do-codigo">{c.id.slice(0, 8)}</span> sem nome
                          </>
                        )
                      )}
                    </span>
                    {/*
                      Mediana em destaque, e ao lado a composição: "15 d" sobre 40 requisições em
                      aberto e sobre 40 que já viraram pedido são leituras diferentes.
                    */}
                    <span>
                      <strong>{c.mediana ?? '—'} d</strong> · {formatarNumero(c.emAberto ?? 0)} em aberto,{' '}
                      {formatarNumero(c.finalizadas ?? 0)} viraram pedido
                    </span>
                  </div>
                  <div className="do-medidor">
                    <div
                      className="do-medidor-preenchimento"
                      style={{ width: `${((c.mediana ?? 0) / maior) * 100}%` }}
                    />
                  </div>
                </li>
              );
            })}
          </ul>
        </>
      )}
    </CardGrafico>
  );
}

export function Fornecedores({
  estado,
  aoAbrirFornecedor,
}: {
  estado: BlocoCarregado<FornecedoresCompras>;
  /** Abre os pedidos que compõem a linha. `pk` nula é a linha "Fornecedor não identificado". */
  aoAbrirFornecedor?: (fornecedor: { pk: string | null; nome: string | null }) => void;
}) {
  if (estado.erro) return <FaixaDeErro oQue="os fornecedores" />;

  const fornecedores = estado.dados?.fornecedores ?? [];

  /*
   * Sem descrição, por pedido do Victor (21/09/2026).
   *
   * ⚠️ A ressalva que estava aqui — o ERP não registra a chegada da peça de forma comparável com a
   * data prometida — **continua no drawer "Como Funciona"** e no bloco "Entrega no prazo", que é
   * onde o assunto vive. O que saiu foi a repetição no card, não a informação.
   */
  return (
    <CardGrafico titulo="Fornecedores">
      {/*
        🔴 at-111 — o backend declarava `filtrosNaoAplicados` desde a at-100 e a tela NÃO o
        renderizava. ⚠️ at-115: o comprador e o centro de custo passaram a ser aplicados aqui (pelo
        elo da at-109), então o que resta nesta faixa é o cadastro não sincronizado.
      */}
      <FaixaFiltrosNaoAplicados
        filtros={estado.dados?.filtrosNaoAplicados}
        porque="o cadastro de locais de uso ainda não foi sincronizado."
      />
      {/* Ver o card de evolução: compra direta não pertence a comprador nem a centro de custo. */}
      {estado.dados?.semOrigemConhecida ? (
        <p className="aviso-de-tela">
          {formatarNumero(estado.dados.semOrigemConhecida)}{' '}
          {estado.dados.semOrigemConhecida === 1
            ? 'pedido do período ficou'
            : 'pedidos do período ficaram'}{' '}
          fora deste recorte: são compras diretas, sem requisição de origem.
        </p>
      ) : null}
      {estado.carregando || fornecedores.length === 0 ? (
        <AreaEstado
          carregando={estado.carregando}
          mensagem="Nenhum pedido de compra sincronizado ainda para este período."
        />
      ) : (
        <div className="do-tabela-wrapper">
          <table className="do-tabela">
            <thead>
              <tr>
                <th>Fornecedor</th>
                <th>Pedidos</th>
                <th>Valor</th>
              </tr>
            </thead>
            <tbody>
              {fornecedores.map((f) => (
                <tr key={f.pk ?? 'sem'}>
                  <td title={f.nome ?? undefined}>
                    {aoAbrirFornecedor ? (
                      <button
                        type="button"
                        className="do-celula-link"
                        onClick={() => aoAbrirFornecedor({ pk: f.pk, nome: f.nome })}
                      >
                        {f.nome ?? 'Fornecedor não identificado'}
                      </button>
                    ) : (
                      f.nome ?? 'Fornecedor não identificado'
                    )}
                  </td>
                  <td>{formatarNumero(f.pedidos)}</td>
                  <td>{formatarMoeda(f.valor)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </CardGrafico>
  );
}

/** Linhas por página nos cards com tabela. */
const POR_PAGINA_CARD = 10;

export function Oficinas({
  estado,
  aoAbrirOficina,
  identificacao = 'todas',
  onIdentificacao,
}: {
  estado: BlocoCarregado<OficinasCompras>;
  /** Abre as requisições daquela oficina — inclusive a linha "Sem oficina informada". */
  aoAbrirOficina?: (oficina: string) => void;
  /** O recorte escolhido no filtro do card (at-117). Ausente = todas. */
  identificacao?: IdentificacaoDeOficina;
  onIdentificacao?: (identificacao: IdentificacaoDeOficina) => void;
}) {
  // ⚠️ `useState` antes de qualquer `return` condicional: hook depois de early return quebra a
  // ordem dos hooks entre renders (o card com erro renderizaria menos hooks que o normal).
  const [pagina, setPagina] = useState(1);

  const dados = estado.dados;
  const oficinas = dados?.oficinas ?? [];
  const totalPaginas = Math.max(1, Math.ceil(oficinas.length / POR_PAGINA_CARD));
  // A lista vem inteira do servidor (são dezenas de oficinas, não milhares), então a paginação é
  // no cliente. Diferente das "Últimas requisições", que pagina no servidor porque a base é grande.
  const visiveis = oficinas.slice((pagina - 1) * POR_PAGINA_CARD, pagina * POR_PAGINA_CARD);

  if (estado.erro) return <FaixaDeErro oQue="as requisições por oficina" />;

  const pct =
    dados && dados.cobertura.total > 0
      ? Math.round((dados.cobertura.comOficina / dados.cobertura.total) * 100)
      : 0;

  return (
    <CardGrafico
      titulo="Requisições por oficina"
      descricao={
        'Para onde as peças vão. Entram aqui as compras de peça automotiva; o nome da oficina vem ' +
        'do texto da requisição, e quando falta é porque o comprador não preencheu.'
      }
    >
      {dados?.universoNaoAplicado ? (
        /*
          🔴 O cadastro de classificações ainda não sincronizou, então o card está mostrando a base
          INTEIRA — vidro, reforma predial, material de escritório. Sem esta faixa, o número seria
          o de antes da at-113 e ninguém teria como saber por quê.
        */
        <p className="aviso-de-tela">
          O cadastro de classificações financeiras ainda não foi sincronizado, então este card está
          contando todas as requisições do período — inclusive as que não são compra de peça. Ele se
          corrige sozinho na próxima sincronização de cadastros.
        </p>
      ) : null}
      {dados ? (
        /*
          🔴 A cobertura é obrigatória: sem este aviso, um gráfico feito sobre uma fração da base
          passa por retrato do todo. O denominador é o UNIVERSO das compras de peça (at-113), e é
          por isso que o texto diz "compras de peça" e não "requisições": antes da entrega ele
          contava a base toda e dava 35%.
        */
        <p className="aviso-de-tela">
          Oficina informada em {formatarNumero(dados.cobertura.comOficina)} de{' '}
          {formatarNumero(dados.cobertura.total)} compras de peça ({pct}%).
          {/*
            ⚠️ A frase acima fala do PERÍODO, sempre — é a informação de qualidade de dado, e ela
            não muda porque alguém filtrou a tabela. Quem descreve o que está na tela é a segunda
            frase, e só quando há recorte: sem ela, "639 de 653" ao lado de uma tabela com 14
            linhas pareceria contradição.
          */}
          {dados.recorte ? (
            <>
              {' '}
              Mostrando {formatarNumero(dados.recorte.mostradas)} de{' '}
              {formatarNumero(dados.recorte.de)} — {rotuloDaIdentificacao(identificacao)}.
            </>
          ) : null}
        </p>
      ) : null}
      {/*
        at-117 — o filtro do CARD (pedido do Victor, 27/09/2026). O rótulo diz "só deste card"
        porque o painel tem um filtro geral no topo com vocabulário parecido, e dois controles com
        o mesmo nome e alcances diferentes é o `BUG-R47`.

        ⚠️ `select` simples, não o modal de caixas de seleção do filtro de status: as três opções
        são mutuamente exclusivas, e "Todas" já é a união das outras duas.
      */}
      {onIdentificacao ? (
        <div className="compras-filtro-card">
          <label htmlFor="filtro-identificacao-oficina">Oficina (só deste card)</label>
          <div className="select-wrapper">
            <select
              id="filtro-identificacao-oficina"
              value={identificacao}
              onChange={(e) => {
                // A página volta para a primeira: a lista muda de tamanho, e a página 3 do
                // recorte anterior viraria uma tabela vazia sem explicação.
                setPagina(1);
                onIdentificacao(e.target.value as IdentificacaoDeOficina);
              }}
            >
              <option value="todas">Todas</option>
              <option value="identificadas">Somente oficinas identificadas</option>
              <option value="nao_identificadas">Somente oficinas não identificadas</option>
            </select>
          </div>
        </div>
      ) : null}
      {estado.carregando || oficinas.length === 0 ? (
        <AreaEstado carregando={estado.carregando} mensagem={SEM_DADO} />
      ) : (
        <div className="do-tabela-wrapper">
          <table className="do-tabela">
            <thead>
              <tr>
                <th>Oficina</th>
                <th>Requisições</th>
                <th>Em aberto</th>
                {/*
                  Coluna separada de "em aberto", e não um total só: quem liga para a oficina
                  externa confirmar o recebimento precisa saber o que JÁ VIROU PEDIDO, não o que
                  ainda está sendo comprado.
                */}
                <th>Aguardando entrega</th>
              </tr>
            </thead>
            <tbody>
              {visiveis.map((o) => (
                <tr key={o.oficina}>
                  <td title={o.oficina}>
                    {/*
                      ⚠️ A linha "Sem oficina informada" é clicável como as outras. Ela não é uma
                      grafia, então o servidor tem um `where` próprio para ela (`whereDaOficina`) —
                      sem isso, seria a única linha inerte da tabela, abrindo vazia sobre um número
                      diferente de zero.
                    */}
                    {aoAbrirOficina ? (
                      <button
                        type="button"
                        className="do-celula-link"
                        onClick={() => aoAbrirOficina(o.oficina)}
                      >
                        {o.oficina}
                      </button>
                    ) : (
                      o.oficina
                    )}
                  </td>
                  <td>
                    {formatarNumero(o.requisicoes)}
                    {/*
                      A marca da salvaguarda (at-113). Discreta de propósito — pedido do Victor —,
                      mas por LINHA: é ela que diz ONDE está a mistura, e é assim que alguém nota
                      que uma classificação deveria estar na lista.

                      ⚠️ Sem emoji e colado no número: célula de tabela é uma linha só.
                    */}
                    {o.foraDaLista > 0 ? (
                      <span
                        className="do-celula-marca"
                        title={`${formatarNumero(o.foraDaLista)} destas não estão nas classificações de compra de peça, mas têm oficina preenchida.`}
                      >
                        *
                      </span>
                    ) : null}
                  </td>
                  <td>{formatarNumero(o.abertas)}</td>
                  <td>{formatarNumero(o.aguardandoEntrega)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {totalPaginas > 1 ? (
            <Paginacao
              paginaAtual={pagina}
              totalPaginas={totalPaginas}
              onMudarPagina={setPagina}
            />
          ) : null}
        </div>
      )}
      {/*
        🔴 A nota de rodapé fecha as duas pontas: explica o `*` e diz ONDE o critério é editado.
        Sem a segunda frase, quem abrir este card daqui a um ano e estranhar um número não tem como
        descobrir sozinho onde aquilo se decide — foi o estado em que o `grupo` do local ficou até
        a at-111. Pedido do Victor, 24/09/2026.
      */}
      {dados && !dados.universoNaoAplicado ? (
        <p className="do-nota-janela">
          {dados.foraDaListaTotal > 0
            ? `* ${formatarNumero(dados.foraDaListaTotal)} requisições não estão nas classificações de compra de peça, mas têm oficina preenchida. `
            : ''}
          As classificações financeiras que definem quais compras entram aqui são editadas em
          Administração do Sistema &rarr; Compras.
        </p>
      ) : null}
    </CardGrafico>
  );
}

/**
 * Requisições por PLACA — o agrupamento por veículo pedido pela área de Compras (at-109).
 *
 * Irmão do card de oficinas, com a mesma estrutura e a mesma exigência de cobertura: os dois
 * atributos saem do TEXTO da justificativa, e nenhum dos dois está em toda requisição.
 */
export function RequisicoesPorPlacaBloco({
  estado,
  aoAbrirPlaca,
  situacoes = [],
  onSituacoes,
  busca = '',
  onBusca,
  pagina = 1,
  onPagina,
}: {
  estado: BlocoCarregado<RequisicoesPorPlaca>;
  aoAbrirPlaca?: (placa: string) => void;
  /** Os status escolhidos no filtro do card (at-110, multi na at-112). Lista vazia = todos. */
  situacoes?: string[];
  onSituacoes?: (situacoes: string[]) => void;
  /** O trecho de placa já aplicado (canônico). Quem busca é o servidor — ver abaixo. */
  busca?: string;
  onBusca?: (busca: string) => void;
  /** A página do card, que também é do servidor. */
  pagina?: number;
  onPagina?: (pagina: number) => void;
}) {
  /*
   * 🔴 A busca e a paginação são do SERVIDOR (at-118, `BUG-014`). Até aqui o card buscava
   * localmente nas placas que a rota devolvia — que eram só as 25 primeiras do período, não
   * "a lista inteira" como o comentário daqui afirmava. Em 29/09/2026 a OYF4G25 era a 45ª de 284
   * placas e não era achada. O card de oficinas NÃO tem o defeito: aquela rota devolve tudo.
   *
   * O texto digitado fica local e só vai ao servidor 300 ms depois da última tecla — uma consulta
   * por tecla seria desperdício, e o card entraria em carregamento a cada letra.
   */
  const [texto, setTexto] = useState(busca);
  useEffect(() => {
    if (!onBusca) return;
    const canonico = texto.toUpperCase().replace(/[^A-Z0-9]/g, '');
    if (canonico === busca) return;
    const id = setTimeout(() => onBusca(canonico), 300);
    return () => clearTimeout(id);
  }, [texto, busca, onBusca]);

  const dados = estado.dados;
  const visiveis = dados?.placas ?? [];
  const totalPaginas = dados ? Math.max(1, Math.ceil(dados.total / Math.max(1, dados.porPagina))) : 1;

  if (estado.erro) return <FaixaDeErro oQue="as requisições por placa" />;

  const pct =
    dados && dados.cobertura.total > 0
      ? Math.round((dados.cobertura.comPlaca / dados.cobertura.total) * 100)
      : 0;

  return (
    <CardGrafico
      titulo="Requisições por placa"
      descricao="O que foi comprado para cada veículo. A placa vem do texto da requisição, e nem todas a informam."
    >
      {dados ? (
        /*
          🔴 Mesma regra do card de oficinas: sem a cobertura, o ranking passa por retrato do todo.
          ⚠️ Com o filtro de status aplicado, ela fala do RECORTE — e o texto diz isso, senão a
          fração pareceria descrever o período inteiro ao lado de uma tabela filtrada.
        */
        <p className="aviso-de-tela">
          Placa identificada em {formatarNumero(dados.cobertura.comPlaca)} de{' '}
          {formatarNumero(dados.cobertura.total)} requisições ({pct}%)
          {descricaoDoFiltroDeStatus(situacoes)}.
        </p>
      ) : null}
      {/*
        Os dois controles do card na MESMA linha (pedido do Victor, 21/09/2026): o status e a busca
        por placa são a mesma pergunta em duas dimensões, e empilhados ocupavam duas faixas do card
        para dois campos curtos.
      */}
      {onSituacoes || dados || busca ? (
        <div className="compras-filtros-linha">
          {onSituacoes ? (
            <FiltroDeSituacao
              valores={situacoes}
              onMudar={onSituacoes}
              idDoCampo="filtro-status-placas"
            />
          ) : null}
          {/* ⚠️ Mostrado mesmo quando a busca não acha nada: esconder o campo junto com a lista
              apagaria o texto que explica a lista vazia, e o usuário ficaria sem como corrigi-lo. */}
          {dados || busca ? (
            <div className="compras-filtro-card">
              <label htmlFor="busca-placa">Placa</label>
              {/*
                `premium-text-input` é o campo de texto do projeto (o mesmo do modal de filtros).
                O espaçamento é do container — `do-hint-campo` daria margem própria e desalinharia
                o campo do select ao lado.
              */}
              <input
                id="busca-placa"
                type="text"
                className="premium-text-input"
                placeholder="Buscar placa"
                value={texto}
                onChange={(e) => setTexto(e.target.value)}
              />
            </div>
          ) : null}
        </div>
      ) : null}
      {estado.carregando || visiveis.length === 0 ? (
        <AreaEstado
          carregando={estado.carregando}
          mensagem={
            busca
              ? 'Nenhuma placa com esse trecho.'
              : situacoes.length
                ? `Nenhuma requisição${descricaoDoFiltroDeStatus(situacoes)} no período e no recorte escolhidos.`
                : SEM_DADO
          }
        />
      ) : (
        <div className="do-tabela-wrapper">
          {/*
            at-118: a tabela CABE no card em vez de rolar. As numéricas têm largura fixa e o
            comprador fica com o resto — é ali que a reticência do `td` e o `title` passam a agir.
            Antes, a largura natural das 6 colunas passava da do card e o nome sumia atrás da borda.
          */}
          <table className="do-tabela do-tabela--cabe">
            <colgroup>
              <col style={{ width: 96 }} />
              <col style={{ width: 104 }} />
              <col style={{ width: 64 }} />
              <col style={{ width: 92 }} />
              <col style={{ width: 150 }} />
              <col />
            </colgroup>
            <thead>
              <tr>
                <th>Placa</th>
                <th>Requisições</th>
                <th>Itens</th>
                <th>Em aberto</th>
                <th>Aguardando entrega</th>
                <th>Comprador</th>
              </tr>
            </thead>
            <tbody>
              {visiveis.map((p) => (
                <tr key={p.placa}>
                  <td>
                    {aoAbrirPlaca ? (
                      <button
                        type="button"
                        className="do-celula-link"
                        onClick={() => aoAbrirPlaca(p.placa)}
                      >
                        {p.placa}
                      </button>
                    ) : (
                      p.placa
                    )}
                  </td>
                  <td>{formatarNumero(p.requisicoes)}</td>
                  <td>{formatarNumero(p.itens)}</td>
                  <td>{formatarNumero(p.abertas)}</td>
                  <td>{formatarNumero(p.aguardandoEntrega)}</td>
                  {/*
                    Célula de UMA linha (regra do Victor, 12/08/2026): com vários compradores, os
                    nomes ficam lado a lado com reticências, e o `title` mostra a lista inteira.
                  */}
                  <td title={p.compradores.join(', ') || undefined}>
                    {p.compradores.join(', ') || '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {totalPaginas > 1 && onPagina ? (
            <Paginacao paginaAtual={pagina} totalPaginas={totalPaginas} onMudarPagina={onPagina} />
          ) : null}
        </div>
      )}
    </CardGrafico>
  );
}

export function UltimasRequisicoesBloco({
  estado,
  filtros,
}: {
  estado: BlocoCarregado<UltimasRequisicoes>;
  filtros: FiltrosCompras;
}) {
  const [pagina, setPagina] = useState(1);
  // Páginas seguintes vêm do servidor; a primeira já veio com o painel, e refazer a consulta só
  // para exibi-la custaria uma ida à rede por nada.
  const [outraPagina, setOutraPagina] = useState<BlocoCarregado<UltimasRequisicoes> | null>(null);

  // Trocar o filtro do painel reconta tudo — a página 3 do recorte anterior não existe mais.
  useEffect(() => {
    setPagina(1);
    setOutraPagina(null);
  }, [filtros]);

  useEffect(() => {
    if (pagina === 1) {
      setOutraPagina(null);
      return;
    }
    let cancelado = false;
    setOutraPagina({ dados: null, carregando: true, erro: false });
    fetchRequisicoesDoRecorte({ filtros, pagina, porPagina: POR_PAGINA_CARD })
      .then((dados) => {
        if (!cancelado) setOutraPagina({ dados, carregando: false, erro: false });
      })
      .catch(() => {
        // 🔴 Nunca cair para lista vazia: "não há requisições nesta página" e "a consulta falhou"
        // levam a conclusões opostas (at-60). A faixa de erro abaixo é o que o usuário vê.
        if (!cancelado) setOutraPagina({ dados: null, carregando: false, erro: true });
      });
    return () => {
      cancelado = true;
    };
  }, [pagina, filtros]);

  const emUso = pagina === 1 ? estado : outraPagina ?? estado;
  if (emUso.erro) return <FaixaDeErro oQue="as últimas requisições" />;

  const requisicoes = emUso.dados?.requisicoes ?? [];
  // O total é do PERÍODO inteiro, não da página — vem do backend junto com as linhas.
  const total = estado.dados?.total ?? requisicoes.length;
  const totalPaginas = Math.max(1, Math.ceil(total / POR_PAGINA_CARD));

  return (
    <CardGrafico
      titulo="Últimas requisições"
      descricao="As mais recentes do período. A ordenação é do espelho: a API do Nasajon não ordena."
    >
      {emUso.carregando || requisicoes.length === 0 ? (
        <AreaEstado carregando={emUso.carregando} mensagem={SEM_DADO} />
      ) : (
        <div className="do-tabela-wrapper">
          <table className="do-tabela">
            <thead>
              <tr>
                <th>Nº</th>
                <th>Data</th>
                <th>Solicitante</th>
                <th>Motivo</th>
                <th>Comprador</th>
                {/* at-109, pedido da área de Compras: quantos itens a requisição tem. */}
                <th>Itens</th>
                <th>Situação</th>
              </tr>
            </thead>
            <tbody>
              {requisicoes.map((r) => (
                <tr key={r.codigo}>
                  <td>
                    {r.codigo}
                    <SeloDeComentarios total={r.comentarios} />
                  </td>
                  <td>{r.dataCadastro ? new Date(r.dataCadastro).toLocaleDateString('pt-BR') : '—'}</td>
                  {/*
                    O ERP entrega um identificador, não o nome. Sem o de-para preenchido, mostra o
                    código truncado — nunca um traço, que seria lido como "não há solicitante".
                  */}
                  <td title={r.solicitanteId ?? undefined}>
                    {r.solicitanteNome ?? (r.solicitanteId ? <span className="do-codigo">{r.solicitanteId.slice(0, 8)}</span> : '—')}
                  </td>
                  <td title={r.justificativaResumo ?? undefined}>{r.justificativaResumo ?? '—'}</td>
                  <td>{r.compradorNome ?? '—'}</td>
                  <td>{formatarNumero(r.itens)}</td>
                  <td>
                    <span className={`cilia-badge ${badgeDaSituacao(r.situacao)}`}>
                      {rotuloDaSituacao(r.situacao)}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {totalPaginas > 1 ? (
            <Paginacao
              paginaAtual={pagina}
              totalPaginas={totalPaginas}
              onMudarPagina={setPagina}
            />
          ) : null}
        </div>
      )}
    </CardGrafico>
  );
}

function badgeDaSituacao(situacao: string | null): string {
  if (situacao === 'atendida') return 'badge-verde';
  if (situacao === 'cancelada') return 'badge-vermelho';
  if (situacao === 'pendente') return 'badge-laranja';
  if (situacao === 'pedido_gerado' || situacao === 'parcialmente_atendida') return 'badge-azul';
  return 'badge-cinza';
}

/**
 * A chegada é sincronizada de hora em hora no expediente e todo dia às 04h (at-128). Mais de 2 dias
 * sem sucesso já não é uma noite sem rodar: é a sincronização parada.
 */
export const DIAS_PARA_AVISAR_CHEGADA_PARADA = 2;

/** Dias inteiros entre o instante do último sucesso e agora. */
function diasDesde(iso: string, agora = Date.now()): number {
  return Math.floor((agora - new Date(iso).getTime()) / 864e5);
}

/**
 * ENTREGA NO PRAZO — previsão do PEDIDO contra a entrada da última NOTA (at-125).
 *
 * A fonte é o elo pedido → nota da API do Nasajon (at-128); de 19 a 22/10/2026 era o relatório
 * "Conferência de Data de Entrega" subido à mão. A previsão é a do PEDIDO: a da nota é a própria
 * data da chegada (`BUG-009`).
 *
 * 🔴 Três estados vazios que NÃO podem se parecer, e cada um manda o usuário a um lugar diferente:
 *   - erro de carga → faixa de erro (acionar a TI);
 *   - nunca sincronizado → aponta a aba Sincronização (é o estado logo após o deploy);
 *   - sincronizado, nada no período → frase de período vazio.
 *
 * 🔴 O frescor aparece SEMPRE ("entradas até DD/MM"): sem a data do último sucesso ao lado, uma
 * sincronização parada passa por dado atual.
 *
 * 🔴 Nunca 0% quando nada foi medido: zero seria afirmação sobre os fornecedores.
 */
const FORNECEDORES_POR_PAGINA = 10;

export function EntregaNoPrazoBloco({
  estado,
  aoAbrirFornecedor,
}: {
  estado: BlocoCarregado<EntregaNoPrazo>;
  /** Abre os pedidos medidos daquele fornecedor — notas, previsão, chegada e a diferença. */
  aoAbrirFornecedor?: (fornecedor: string) => void;
}) {
  // ⚠️ Hooks antes de qualquer return condicional. A paginação é local: a resposta já traz todos os
  // fornecedores (emergencial, 20/10/2026 — sem o piso de 5 pedidos a lista ficou longa).
  const [pagina, setPagina] = useState(1);
  useEffect(() => setPagina(1), [estado.dados]);

  if (estado.erro) return <FaixaDeErro oQue="a pontualidade dos fornecedores" />;

  const d = estado.dados;
  const totalPaginasForn = Math.max(
    1,
    Math.ceil((d?.fornecedores.length ?? 0) / FORNECEDORES_POR_PAGINA),
  );
  const fornecedoresDaPagina = (d?.fornecedores ?? []).slice(
    (pagina - 1) * FORNECEDORES_POR_PAGINA,
    pagina * FORNECEDORES_POR_PAGINA,
  );
  const c = d?.cobertura;
  const sinc = d?.sincronizacao;
  const descartados = c ? c.semPrevisao + c.previsaoImpossivel + c.chegadaAntesDaEmissao : 0;
  const diasSemSincronizar = sinc?.ultimoSucessoEm ? diasDesde(sinc.ultimoSucessoEm) : null;

  return (
    <CardGrafico
      titulo="Entrega no prazo"
      descricao="Previsão registrada no pedido de compra contra a data de entrada da nota fiscal, vindas do Nasajon."
    >
      {/*
        Não é aviso de erro nem de carregamento: é o ESCOPO do indicador, e vale mesmo quando tudo
        deu certo. Por isso aparece antes de qualquer estado.
      */}
      <p className="aviso-de-tela">
        Cobre <strong>somente a oficina interna</strong>: a peça que vai direto para oficina externa
        é avisada em grupo de WhatsApp e não gera nota de entrada no Nasajon.
      </p>

      <FaixaFiltrosNaoAplicados
        filtros={d?.filtrosNaoAplicados}
        porque="o cadastro de locais de uso ainda não foi sincronizado."
      />

      {diasSemSincronizar !== null && diasSemSincronizar > DIAS_PARA_AVISAR_CHEGADA_PARADA ? (
        <p className="aviso-de-tela">
          A chegada dos pedidos não é sincronizada com o Nasajon há{' '}
          <strong>{diasSemSincronizar} dias</strong>: este indicador pode estar desatualizado. Veja a
          aba Sincronização.
        </p>
      ) : null}

      {estado.carregando ? (
        <AreaEstado carregando mensagem="" />
      ) : sinc && sinc.ultimoSucessoEm === null ? (
        <AreaEstado
          carregando={false}
          mensagem="A chegada dos pedidos ainda não foi sincronizada com o Nasajon. Ela roda sozinha de hora em hora; acompanhe na aba Sincronização."
        />
      ) : !d?.disponivel ? (
        <AreaEstado
          carregando={false}
          mensagem="Nenhum pedido com entrada no período escolhido pôde ser medido."
        />
      ) : (
        <>
          <div className="do-kpis">
            <Kpi
              rotulo="No prazo"
              valor={`${d.percentualNoPrazo}%`}
              apoio={`${formatarNumero(d.noPrazo)} de ${formatarNumero(c?.medidas ?? 0)} pedidos`}
            />
            <Kpi
              rotulo="Atrasados"
              valor={formatarNumero(d.atrasadas)}
              apoio={
                d.medianaDiasDeAtraso != null
                  ? `mediana de ${d.medianaDiasDeAtraso} d de atraso`
                  : 'sem atraso medido'
              }
            />
          </div>

          {c ? (
            <p className="do-nota-janela">
              {formatarNumero(c.medidas)} de {formatarNumero(c.pedidosNoPeriodo)} pedidos com
              entrada no período puderam ser medidos.
              {descartados > 0
                ? ` Ficaram de fora ${formatarNumero(c.semPrevisao)} sem previsão, ${formatarNumero(c.previsaoImpossivel)} com previsão anterior à emissão e ${formatarNumero(c.chegadaAntesDaEmissao)} com entrada anterior à emissão do pedido.`
                : ''}
              {c.previsaoNoDiaDaEmissao > 0
                ? ` ${formatarNumero(c.previsaoNoDiaDaEmissao)} têm previsão no próprio dia da emissão e foram contados.`
                : ''}
            </p>
          ) : null}

          {c?.semOrigemConhecida ? (
            <p className="aviso-de-tela">
              {formatarNumero(c.semOrigemConhecida)}{' '}
              {c.semOrigemConhecida === 1 ? 'pedido ficou' : 'pedidos ficaram'} fora deste recorte:
              não têm requisição de origem conhecida, então não pertencem a comprador nem a centro
              de custo.
            </p>
          ) : null}

          {d.fornecedores.length ? (
            <div className="do-tabela-wrapper">
              <table className="do-tabela">
                <thead>
                  <tr>
                    <th>Fornecedor</th>
                    <th>Pedidos</th>
                    <th>No prazo</th>
                    <th>Atraso médio</th>
                  </tr>
                </thead>
                <tbody>
                  {fornecedoresDaPagina.map((f) => (
                    <tr key={f.fornecedor}>
                      <td title={f.fornecedor}>
                        {aoAbrirFornecedor ? (
                          <button
                            type="button"
                            className="do-celula-link"
                            onClick={() => aoAbrirFornecedor(f.fornecedor)}
                          >
                            {f.fornecedor}
                          </button>
                        ) : (
                          f.fornecedor
                        )}
                      </td>
                      <td>{formatarNumero(f.entregas)}</td>
                      <td>{f.percentualNoPrazo}%</td>
                      <td>{f.atrasoMedioDias > 0 ? `${f.atrasoMedioDias} d` : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {totalPaginasForn > 1 ? (
                <Paginacao
                  paginaAtual={pagina}
                  totalPaginas={totalPaginasForn}
                  onMudarPagina={setPagina}
                />
              ) : null}
            </div>
          ) : (
            <AreaEstado
              carregando={false}
              mensagem="Nenhum fornecedor com pedido medido no período."
            />
          )}
        </>
      )}

      {sinc?.ultimoSucessoEm ? (
        <p className="do-nota-janela">
          Entradas até <strong>{formatarDataPura(sinc.entradasAte)}</strong> · sincronizado com o
          Nasajon em {formatarInstante(sinc.ultimoSucessoEm)}.
        </p>
      ) : null}
    </CardGrafico>
  );
}

/* ------------------------------------------------------------------------------------------------
 * at-111 — os três blocos do painel por setor.
 *
 * O primeiro existe nos três modos do filtro; os outros dois ocupam, em "Outros setores", o lugar
 * dos cards de oficina e placa. O porquê está medido: naquele recorte a oficina aparece em 2 de
 * 790 requisições e a placa é ruído do terceiro degrau do parser (21/09/2026).
 * --------------------------------------------------------------------------------------------- */

/**
 * Requisições por SETOR requisitante.
 *
 * 🔴 É o card que prova, na tela, o que o Victor pediu: a soma das linhas é o total do período, em
 * qualquer modo do filtro. A linha **"Local não informado"** é parte disso — são as requisições
 * sem local de estoque no ERP, que antes desta entrega não apareciam em Peças nem em Outros.
 */
export function RequisicoesPorCentroCustoBloco({
  estado,
  aoAbrirCentro,
}: {
  estado: BlocoCarregado<RequisicoesPorCentroCusto>;
  aoAbrirCentro?: (centro: { centroCusto: string | null; descricao: string }) => void;
}) {
  // ⚠️ `useState` antes de qualquer return condicional (a ordem dos hooks entre renders).
  const [pagina, setPagina] = useState(1);

  const centros = estado.dados?.centros ?? [];
  const totalPaginas = Math.max(1, Math.ceil(centros.length / POR_PAGINA_CARD));
  const visiveis = centros.slice((pagina - 1) * POR_PAGINA_CARD, pagina * POR_PAGINA_CARD);

  // 🔴 Antes de qualquer número: tabela vazia aqui seria lida como "não há requisições no período".
  if (estado.erro) return <FaixaDeErro oQue="as requisições por centro de custo" />;

  return (
    <CardGrafico
      titulo="Requisições por centro de custo"
      descricao="Quem paga a compra, pelo centro de custo do local de uso no Nasajon. Clique para ver as requisições da linha."
    >
      {/*
        🔴 Estado próprio, e não a tabela vazia: enquanto a camada CADASTROS não rodar, TUDO cai
        em "Sem centro de custo" — um painel que parece dizer "a empresa não classifica nada",
        quando o que houve foi uma sincronização que ainda não aconteceu.
      */}
      {estado.dados?.cadastroVazio ? (
        <p className="aviso-de-tela">
          O cadastro de centros de custo ainda não foi sincronizado — por isso tudo aparece em
          &quot;Sem centro de custo&quot;. Isto não é o estado real da classificação.
        </p>
      ) : null}
      {estado.carregando || centros.length === 0 ? (
        <AreaEstado carregando={estado.carregando} mensagem={SEM_DADO} />
      ) : (
        <div className="do-tabela-wrapper">
          <table className="do-tabela">
            <thead>
              <tr>
                <th>Centro de custo</th>
                <th>Código</th>
                <th>Requisições</th>
                <th>Em aberto</th>
                <th>Aguardando entrega</th>
              </tr>
            </thead>
            <tbody>
              {visiveis.map((c) => (
                <tr key={c.centroCusto ?? 'sem-centro'}>
                  <td title={c.descricao}>
                    {aoAbrirCentro ? (
                      <button
                        type="button"
                        className="do-celula-link"
                        onClick={() =>
                          aoAbrirCentro({ centroCusto: c.centroCusto, descricao: c.descricao })
                        }
                      >
                        {c.descricao}
                      </button>
                    ) : (
                      c.descricao
                    )}
                  </td>
                  <td>{c.codigo ?? '—'}</td>
                  <td>{formatarNumero(c.requisicoes)}</td>
                  <td>{formatarNumero(c.abertas)}</td>
                  <td>{formatarNumero(c.aguardandoEntrega)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {totalPaginas > 1 ? (
            <Paginacao paginaAtual={pagina} totalPaginas={totalPaginas} onMudarPagina={setPagina} />
          ) : null}
        </div>
      )}
    </CardGrafico>
  );
}

/**
 * Requisições por SOLICITANTE — quem pediu.
 *
 * Irmão do card de oficinas na estrutura e na exigência de cobertura: o solicitante não está em
 * toda requisição, e um ranking sobre parte da base, sem dizer isso, passa por retrato do todo.
 */
export function RequisicoesPorSolicitanteBloco({
  estado,
  aoAbrirSolicitante,
}: {
  estado: BlocoCarregado<RequisicoesPorSolicitante>;
  aoAbrirSolicitante?: (pessoa: { id: string; nome: string | null }) => void;
}) {
  const [pagina, setPagina] = useState(1);

  const dados = estado.dados;
  const solicitantes = dados?.solicitantes ?? [];
  const totalPaginas = Math.max(1, Math.ceil(solicitantes.length / POR_PAGINA_CARD));
  const visiveis = solicitantes.slice((pagina - 1) * POR_PAGINA_CARD, pagina * POR_PAGINA_CARD);

  if (estado.erro) return <FaixaDeErro oQue="as requisições por solicitante" />;

  const pct =
    dados && dados.cobertura.total > 0
      ? Math.round((dados.cobertura.comSolicitante / dados.cobertura.total) * 100)
      : 0;

  return (
    <CardGrafico
      titulo="Requisições por solicitante"
      descricao="Quem pediu, e de qual setor. Clique no nome para ver as requisições da pessoa."
    >
      {dados ? (
        <p className="aviso-de-tela">
          Solicitante identificado em {formatarNumero(dados.cobertura.comSolicitante)} de{' '}
          {formatarNumero(dados.cobertura.total)} requisições ({pct}%).
        </p>
      ) : null}
      {estado.carregando || solicitantes.length === 0 ? (
        <AreaEstado carregando={estado.carregando} mensagem={SEM_DADO} />
      ) : (
        <div className="do-tabela-wrapper">
          <table className="do-tabela">
            <thead>
              <tr>
                <th>Solicitante</th>
                <th>Setor</th>
                <th>Requisições</th>
                <th>Em aberto</th>
                <th>Aguardando entrega</th>
              </tr>
            </thead>
            <tbody>
              {visiveis.map((s) => (
                <tr key={s.id}>
                  <td title={s.nome ?? s.id}>
                    {aoAbrirSolicitante ? (
                      <button
                        type="button"
                        className="do-celula-link"
                        onClick={() => aoAbrirSolicitante({ id: s.id, nome: s.nome })}
                      >
                        {s.nome ?? 'Pessoa não identificada no Nasajon'}
                      </button>
                    ) : (
                      s.nome ?? 'Pessoa não identificada no Nasajon'
                    )}
                  </td>
                  {/* Célula é UMA linha: os setores vão lado a lado, com o valor inteiro no hover. */}
                  <td title={s.setores.join(', ')}>{s.setores.join(', ')}</td>
                  <td>{formatarNumero(s.requisicoes)}</td>
                  <td>{formatarNumero(s.abertas)}</td>
                  <td>{formatarNumero(s.aguardandoEntrega)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {totalPaginas > 1 ? (
            <Paginacao paginaAtual={pagina} totalPaginas={totalPaginas} onMudarPagina={setPagina} />
          ) : null}
        </div>
      )}
    </CardGrafico>
  );
}

/**
 * O que mais se pede — itens por descrição.
 *
 * ⚠️ **Sem valor, e isso é dito na tela.** O `valor_unitario` do item de requisição vem zerado em
 * 100% dos casos (medido em 22/09/2026): o preço nasce no pedido de compra. Uma coluna de moeda
 * aqui exibiria R$ 0,00 com cara de fato.
 */
export function ItensMaisRequisitadosBloco({
  estado,
}: {
  estado: BlocoCarregado<ItensMaisRequisitados>;
}) {
  if (estado.erro) return <FaixaDeErro oQue="os itens mais requisitados" />;

  const dados = estado.dados;
  const itens = dados?.itens ?? [];

  return (
    <CardGrafico
      titulo="Itens mais requisitados"
      descricao="O que mais aparece nas requisições do período, pela descrição digitada no Nasajon."
    >
      <p className="aviso-de-tela">
        Sem valor: a requisição <strong>não tem preço</strong> — ele nasce no pedido de compra.
      </p>
      {estado.carregando || itens.length === 0 ? (
        <AreaEstado carregando={estado.carregando} mensagem={SEM_DADO} />
      ) : (
        <div className="do-tabela-wrapper">
          <table className="do-tabela">
            <thead>
              <tr>
                <th>Item</th>
                <th>Requisições</th>
                <th>Quantidade</th>
              </tr>
            </thead>
            <tbody>
              {itens.map((i) => (
                <tr key={i.descricao}>
                  <td title={i.descricao}>{i.descricao}</td>
                  <td>{formatarNumero(i.requisicoes)}</td>
                  <td>{formatarNumero(i.quantidade)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {dados ? (
            <p className="do-nota-janela">
              {/*
                🔴 O corte é declarado: "os itens são estes" sobre um conjunto maior é pior do que
                não listar, porque parece completo.
              */}
              Mostrando {formatarNumero(itens.length)} de {formatarNumero(dados.totalDeItens)} itens
              distintos. Itens sincronizados em{' '}
              {formatarNumero(dados.cobertura.requisicoesComItem)} de{' '}
              {formatarNumero(dados.cobertura.total)} requisições.
            </p>
          ) : null}
        </div>
      )}
    </CardGrafico>
  );
}
